import type { EligibleAssignee, NotifyOption } from '../../lib/taskAssign'
import { NOTIFY_CHOICES } from '../../lib/taskAssign'

// ============================================================
// Assign controls — MemberPicker + NotifySelect
// ============================================================
// Shared by the assign flows in Price Intelligence, Alert Feed, and the Action
// Queue. The MemberPicker replaces the old free-text "assign to" input: it lists
// only active, location-authorized teammates (name + email, never phone) fetched
// from GET /annotations/eligible-assignees. The NotifySelect is the four-option
// notification choice. Both are controlled + presentational — the parent owns
// state and submits.

const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em',
  textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 6,
}
const selectStyle: React.CSSProperties = {
  width: '100%', padding: '9px 11px', borderRadius: 9, background: 'var(--surface-2)',
  border: '1px solid var(--border-2)', color: 'var(--text-1)', fontFamily: 'var(--sans)',
  fontSize: 13, cursor: 'pointer',
}

export function MemberPicker({
  assignees, value, onChange, loading, disabled, emptyHint,
}: {
  assignees: EligibleAssignee[]
  value: string | null
  onChange: (userId: string | null) => void
  loading?: boolean
  disabled?: boolean
  emptyHint?: string
}) {
  const isEmpty = !loading && assignees.length === 0
  return (
    <div>
      <label style={labelStyle} htmlFor="assign-member">Assign to</label>
      <select
        id="assign-member"
        data-testid="assign-member"
        value={value ?? ''}
        disabled={disabled || loading || isEmpty}
        onChange={(e) => onChange(e.target.value || null)}
        style={{ ...selectStyle, opacity: disabled || loading || isEmpty ? 0.6 : 1 }}
      >
        <option value="">{loading ? 'Loading teammates…' : isEmpty ? 'No teammates available' : 'Unassigned'}</option>
        {assignees.map((a) => (
          <option key={a.user_id} value={a.user_id}>
            {a.name}{a.email ? ` · ${a.email}` : ''}{a.all_locations ? ' · all locations' : ''}
          </option>
        ))}
      </select>
      {isEmpty && (
        <div style={{ marginTop: 6, fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--text-3)', lineHeight: 1.5 }}>
          {emptyHint ?? 'No teammates are set up for this location yet. You can still file the task as a note.'}
        </div>
      )}
    </div>
  )
}

export function NotifySelect({
  value, onChange, disabled,
}: {
  value: NotifyOption
  onChange: (v: NotifyOption) => void
  disabled?: boolean
}) {
  return (
    <div>
      <label style={labelStyle}>Notify them</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {NOTIFY_CHOICES.map((c) => {
          const active = value === c.value
          return (
            <button
              key={c.value}
              type="button"
              data-testid={`notify-${c.value}`}
              disabled={disabled}
              onClick={() => onChange(c.value)}
              title={c.hint}
              style={{
                padding: '7px 12px', borderRadius: 8, cursor: disabled ? 'default' : 'pointer',
                fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600,
                border: `1px solid ${active ? 'var(--accent)' : 'var(--border-2)'}`,
                background: active ? 'var(--accent-soft)' : 'var(--surface-2)',
                color: active ? 'var(--accent)' : 'var(--text-2)', opacity: disabled ? 0.5 : 1,
              }}
            >
              {c.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// A compact, read-only presentation of per-channel delivery outcomes — used right
// after submit and on the Action Queue. Never shows an email or phone.
export function DeliveryChips({ summary, tone }: { summary: string | null; tone: 'ok' | 'warn' | 'bad' | null }) {
  if (!summary) return null
  const color = tone === 'bad' ? 'var(--danger)' : tone === 'warn' ? 'var(--warm)' : 'var(--accent)'
  return (
    <span
      data-testid="delivery-chip"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'var(--mono)', fontSize: 9,
        fontWeight: 700, color, letterSpacing: '0.03em',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
      {summary}
    </span>
  )
}
